/**
 * Edit Organization Address Form Composer
 *
 * Form for editing existing addresses for an organization.
 * Supports read-only mode when address is archived.
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation } from '@/api/ApiQueryServiceProvider'
import {
  AddOrganizationAddressFormSchema,
  transformToApiRequest,
  validateField,
  FORM_CONFIG,
  type AddOrganizationAddressFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderButtonControl,
  type ControlConfig,
} from '../../formRenderFunctions'
import { PhoneInputField } from '@/components/ui/phone-input'
import { CountryDropdown, G20_COUNTRIES } from '@/components/ui/country-dropdown'
import { useToast } from '@/contexts/ToastContext'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type EditOrganizationAddressComposerProps = {
  organizationId: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues: AddOrganizationAddressFormData & { isArchived?: boolean; addressNumber?: number }
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Composer Component
// ============================================================================

export function EditOrganizationAddressComposer(props: EditOrganizationAddressComposerProps) {
  const { organizationId, onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props
  const { toast } = useToast()

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['addressName', 'addressType', 'street', 'city', 'state', 'postalCode', 'country'].includes(name)
  }

  const isReadOnly = !!initialValues?.isArchived

  const upsertAddress = useApiMutation('upsertOrganizationAddress', 'POST', {
    invalidates: ['getOrganization'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to save address:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddOrganizationAddressFormSchema,
    defaultValues: {
      addressName: initialValues?.addressName ?? '',
      addressType: initialValues?.addressType ?? '',
      phone: initialValues?.phone ?? '',
      street: initialValues?.street ?? '',
      city: initialValues?.city ?? '',
      state: initialValues?.state ?? '',
      postalCode: initialValues?.postalCode ?? '',
      country: initialValues?.country ?? '',
      id: initialValues?.id,
      addressNumber: initialValues?.addressNumber,
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddOrganizationAddressFormData
      if (isReadOnly) {
        return
      }

      const parsed = AddOrganizationAddressFormSchema.safeParse({
        addressName: formValue.addressName?.trim() ?? '',
        addressType: formValue.addressType,
        phone: formValue.phone?.trim() ?? '',
        street: formValue.street?.trim() ?? '',
        city: formValue.city?.trim() ?? '',
        state: formValue.state?.trim() ?? '',
        postalCode: formValue.postalCode?.trim() ?? '',
        country: formValue.country?.trim() ?? '',
        id: formValue.id,
        addressNumber: formValue.addressNumber,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        // Surface WHY the form didn't submit instead of returning silently.
        const first = parsed.error.issues[0]
        toast({
          title: 'Cannot save address',
          description: first
            ? `${String(first.path[0] ?? 'Field')}: ${first.message}`
            : 'Please fix the highlighted fields.',
          type: 'error',
        })
        return
      }

      const apiRequest = transformToApiRequest(parsed.data, organizationId)
      await upsertAddress.mutateAsync(apiRequest)
    },
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddOrganizationAddressFormData, value) || undefined,
        }}
      >
        {(bound) => {
          const baseConfig: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: isReadOnly || (control.disabled ?? false),
          }

          if (control.htmlControl === 'select') {
            return renderSelectControl(
              {
                ...baseConfig,
                options: control.selectOptions || [],
              },
              bound,
            )
          }

          if (control.htmlControl === 'phone') {
            return (
              <PhoneInputField
                label={control.label}
                value={(bound.state.value as string) ?? ''}
                onChange={(val) => bound.handleChange(val ?? '')}
                required={isRequired}
                disabled={isReadOnly || (control.disabled ?? false)}
                error={bound.state.meta.errors?.[0] as string | undefined}
              />
            )
          }

          if (control.htmlControl === 'country') {
            return (
              <CountryDropdown
                label={control.label}
                value={(bound.state.value as string) ?? ''}
                onChange={(country) => bound.handleChange(country?.name ?? '')}
                options={G20_COUNTRIES}
                required={isRequired}
                disabled={isReadOnly || (control.disabled ?? false)}
                error={bound.state.meta.errors?.[0] as string | undefined}
              />
            )
          }

          return renderTextControl(baseConfig, bound)
        }}
      </form.AppField>
    )
  }

  const renderFormContent = () => {
    return groups.map((group) => {
      const groupControls = controlsByGroup.get(group.formGroupName) || []
      if (groupControls.length === 0) return null

      return (
        <div key={group.formGroupName} className="space-y-4">
          {groupControls.map(renderControl)}
        </div>
      )
    })
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{isReadOnly ? 'View Address' : 'Edit Address'}</h2>
            <p className="text-sm text-muted-foreground">
              {isReadOnly ? 'This address is archived and read-only.' : 'Update address details below.'}
            </p>
          </div>
        )}

        {renderFormContent()}

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button',
          })}

          {renderButtonControl(
            {
              label: upsertAddress.isPending
                ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
                : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
              variant: 'default',
              type: 'submit',
              disabled: isReadOnly,
            },
            { SubmitButton: form.SubmitButton },
          )}
        </div>
      </div>
    </form.AppForm>
  )
}

export type { AddOrganizationAddressFormData } from '../addOrganizationAddress/schema'
